import React from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ChevronRight, AlertCircle, Gift, Monitor, Video } from "lucide-react";
import type { HomeBannersState } from "../hooks/useHomeBanners";
import { TiendaNubeIcon } from "./TiendaNubeIcon";
import { TiendaNubeConnectModal } from "./TiendaNubeConnectModal";
import CuestionarioModal from "./CuestionarioModal";
import { ProbarEnEscritorioModal } from "./ProbarEnEscritorioModal";

/**
 * Render de los banners de home (prueba gratis / describir la marca). Es
 * presentacional: recibe el `state` resuelto por `useHomeBanners` (que se llama
 * UNA vez por pantalla) para no duplicar el fetch cuando se rendea en mobile y
 * desktop a la vez.
 *
 * El diseño es el mismo que tenía la pantalla clásica de selección de productos
 * (banner responsive: stack en mobile, fila en desktop).
 */
export const HomeBannersView: React.FC<{
  state: HomeBannersState;
  /** Clases extra para el contenedor (ej. `sm:hidden` / `hidden sm:block`). */
  className?: string;
  /** Quita el margen inferior del banner (para layouts en fila, no apilados). */
  noMargin?: boolean;
  /** Si true, muestra el banner de ESTADO de la suscripción (cancelación
   *  programada / pausada) con prioridad sobre los promocionales. Hoy solo se
   *  pasa en las instancias mobile. */
  showStatus?: boolean;
}> = ({ state, className, noMargin, showStatus }) => {
  const navigate = useNavigate();
  // `freeImagesRemaining` / `creditsTotal` / `discountPercent` siguen en el estado
  // —los consume `CreditsTopBar`, que el llamador monta aparte— pero acá ya no se
  // leen: eran del banner `upsell` (ADR-2026-08-03-barra-creditos-reemplaza-upsell).
  const {
    kind,
    idCompany,
    status,
    cancelDate,
    resumeDate,
    cancelTrigger,
  } = state;

  const mb = noMargin ? "" : "mb-4";
  /**
   * Tratamiento de los CTA de los carteles post-suscripción —
   * ADR-2026-08-03-cta-de-carteles-en-tratamiento-suave.
   *
   * Suave (lavanda + texto azul) y no sólido: los tres carteles son sugerencias —
   * describí tu marca, respondé la encuesta, conectá tu tienda—, no cosas que haya
   * que resolver. Con el azul lleno competían con "Mejorar plan", que sí lo es.
   * Es el mismo par de colores que usa `clasesMejorarPlan` en su rama atenuada; se
   * repite el literal en vez de importarlo porque esa función vive en una PANTALLA
   * (`EstiloMinimal`) y este componente no puede depender de una.
   *
   * Sin `shadow-sm`: en un botón sin fondo sólido la sombra lo despega del cartel y
   * se lee como una tarjeta arriba de otra.
   *
   * **Sin alto ni padding horizontal**: los pone cada botón —
   * ADR-2026-08-04-cta-solo-icono-es-cuadrado. Los que en mobile quedan reducidos a la
   * flecha (marca, encuesta) son `CTA_ICONO`: un cuadrado de 36×36 con la flecha
   * centrada. Con el `px-4` acá adentro sobraban 16px a cada lado de un ícono de 16 —
   * el botón terminaba midiendo 48 de ancho por 40 de alto para mostrar una flecha—, y
   * ni el padding ni el alto se pueden pisar desde la clase del llamador: `px-0` y
   * `px-4` (o `h-9` y `h-10`) compiten por la misma propiedad, y ahí gana el orden de
   * la hoja de Tailwind, no el del atributo `class`.
   */
  const CTA_SUAVE =
    "inline-flex items-center justify-center gap-2 rounded-xl bg-[#EBEFFE] text-brand-primary hover:bg-[#DFE6FD] transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary";
  /**
   * El CTA de los carteles de una sola acción: cuadrado y más bajo en mobile (36×36,
   * proporcionado al título de 14px que tiene al lado), con su etiqueta y los 40px de
   * siempre en `sm:`.
   */
  const CTA_ICONO = `${CTA_SUAVE} h-9 w-9 sm:h-10 sm:w-auto sm:px-4`;
  // Cancelación por rechazo de pago (fondos insuficientes): copy distinto.
  const byPaymentFailure = cancelTrigger === "fondos_insuficientes";

  // Modal Conectar Tiendanube (banner "tiendanube"): mismo flujo que Mi marca,
  // sin navegar. Estado local por instancia (solo la visible recibe el click).
  const [showTnConnect, setShowTnConnect] = React.useState(false);
  const [tnConnecting, setTnConnecting] = React.useState(false);
  const [tnError, setTnError] = React.useState<string | null>(null);
  // Modal del cuestionario (banner "cuestionario"). Mismo patrón que el de
  // Tiendanube: estado local por instancia — `HomeBannersView` se rendea dos
  // veces por pantalla (mobile + desktop) y solo la visible recibe el click.
  const [showCuestionario, setShowCuestionario] = React.useState(false);
  // El banner se baja al CERRAR el modal, no al enviar: `dismissCuestionario`
  // pone `kind` en null y este componente hace `return null` cuando no hay
  // kind, lo que desmontaría el modal antes de que el usuario llegue a ver la
  // pantalla de "¡Gracias!". Un ref evita depender del timing del re-render.
  const cuestionarioDoneRef = React.useRef(false);
  // Modal "Probá Atlas en tu computadora" (banner "escritorio"). Mismo patrón de
  // estado local por instancia que los dos de arriba.
  const [showEscritorio, setShowEscritorio] = React.useState(false);
  const handleTnConnect = async () => {
    if (tnConnecting) return;
    setTnConnecting(true);
    setTnError(null);
    try {
      await state.connectTiendanube?.(); // al éxito el hook oculta el banner
      setShowTnConnect(false);
    } catch (e) {
      // Antes esto era un `catch` vacío: con la ventana bloqueada el banner
      // quedaba igual y el usuario no se enteraba de nada
      // (ADR-2026-08-07-tiendanube-popup-safari).
      const msg = e instanceof Error ? e.message : "No se pudo conectar.";
      // Cerrar la ventana a propósito no es un error que haya que cantarle.
      setTnError(msg === "Conexión cancelada." ? null : msg);
    } finally {
      setTnConnecting(false);
    }
  };

  // ── Banner de estado de suscripción (prioridad sobre los promocionales) ──
  // Cancelación programada (ámbar) — espeja la vista del Paywall.
  if (showStatus && status === "cancelled") {
    return (
      <div className={className}>
        <div
          className={`${mb} bg-amber-50 border border-amber-200 rounded-2xl p-4 relative sm:flex sm:items-center sm:justify-between sm:gap-3`}
        >
          <div className="pb-12 sm:pb-0">
            <div className="flex items-center gap-2 mb-0.5 sm:mb-1">
              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
              <h3 className="text-[14px] font-semibold text-brand-black/90 sm:text-[15px]">
                Cancelación programada
              </h3>
            </div>
            <p className="text-[13px] leading-snug text-[#3C3C43]/60 sm:text-[15px] sm:leading-normal">
              Tu plan se cancelará el{" "}
              <strong className="font-medium text-brand-black/80">
                {cancelDate}
              </strong>
              {byPaymentFailure
                ? " por rechazo de tu último pago."
                : ". No se te realizará ningún cobro adicional."}
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/subscription")}
            className="absolute bottom-4 right-4 sm:relative sm:bottom-auto sm:right-auto inline-flex items-center gap-1.5 h-10 px-4 bg-white border border-neutral-200 text-brand-black/90 rounded-xl shadow-sm text-[13.5px] font-medium hover:bg-neutral-50 transition-colors flex-shrink-0 sm:text-[15px]"
          >
            Ver detalles
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }
  // Suscripción pausada (azul) — espeja la vista del Paywall.
  if (showStatus && status === "paused") {
    return (
      <div className={className}>
        <div
          className={`${mb} bg-blue-50 border border-blue-200 rounded-2xl p-4 relative sm:flex sm:items-center sm:justify-between sm:gap-3`}
        >
          <div className="pb-12 sm:pb-0">
            <div className="flex items-center gap-2 mb-0.5 sm:mb-1">
              <AlertCircle className="w-4 h-4 text-blue-600 shrink-0" />
              <h3 className="text-[14px] font-semibold text-brand-black/90 sm:text-[15px]">
                Suscripción pausada
              </h3>
            </div>
            <p className="text-[13px] leading-snug text-[#3C3C43]/60 sm:text-[15px] sm:leading-normal">
              Tu plan se reanudará el{" "}
              <strong className="font-medium text-brand-black/80">
                {resumeDate}
              </strong>
              . No se te cobrará durante la pausa.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/subscription")}
            className="absolute bottom-4 right-4 sm:relative sm:bottom-auto sm:right-auto inline-flex items-center gap-1.5 h-10 px-4 bg-white border border-neutral-200 text-brand-black/90 rounded-xl shadow-sm text-[13.5px] font-medium hover:bg-neutral-50 transition-colors flex-shrink-0 sm:text-[15px]"
          >
            Ver detalles
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  if (kind === null) return null;

  /* Acá vivía el banner `upsell` de la prueba gratis, con sus tres lecturas según
     saldo y cupón. Lo reemplazó la barra superior de créditos
     (ADR-2026-08-03-barra-creditos-reemplaza-upsell): decía lo mismo que ella —"te
     quedan N", "te quedaste sin", el % del cupón— en un formato distinto y en la
     misma pantalla. La lectura que NO tenía equivalente ("¿Te está gustando Atlas?",
     plan gratis con saldo de sobra) se dio de baja a propósito: sin nada que
     resolver, el empuje al plan queda en el CTA "Mejorar plan" del encabezado, que
     está siempre. */

  // kind === "capacitacion": la llamada de capacitación incluida del plan ULTRA
  // (ADR-2026-08-25-banner-llamada-capacitacion-ultra). Mismo shell que
  // "tiendanube"/"escritorio" porque son DOS acciones. Las dos marcan el flag
  // (`dismissCapacitacion`): tocó "Agendar" o "No me interesa" → el cartel no
  // vuelve, y la cadena se reevalúa para que el siguiente aparezca en el acto.
  if (kind === "capacitacion") {
    return (
      <div className={className}>
        <div
          className={`${mb} bg-white border border-neutral-200 rounded-2xl p-4 relative sm:flex sm:items-center sm:justify-between sm:gap-3`}
        >
          <div className="pb-14 sm:pb-0">
            <div className="flex items-center gap-2 mb-0.5 sm:mb-1">
              <Video className="w-4 h-4 text-brand-primary shrink-0" />
              <h3 className="text-[14px] font-semibold text-brand-black/90 sm:text-[15px]">
                Agendá tu videollamada de capacitación
              </h3>
            </div>
            <p className="text-[13px] leading-snug text-[#3C3C43]/60 sm:text-[15px] sm:leading-normal">
              Tu plan Ultra incluye una videollamada personalizada para
              sacarle el máximo provecho a Atlas.
            </p>
          </div>
          <div className="absolute bottom-4 left-4 right-4 flex items-center justify-end gap-2 sm:relative sm:bottom-auto sm:left-auto sm:right-auto sm:w-auto">
            <button
              type="button"
              onClick={() => state.dismissCapacitacion?.()}
              className="inline-flex items-center justify-center h-10 px-3 text-[13.5px] font-medium text-[#3C3C43]/60 hover:text-brand-black/80 rounded-xl transition-colors flex-shrink-0 sm:text-[15px]"
            >
              No me interesa
            </button>
            {/* "Agendar" también da el cartel por resuelto ANTES de navegar: la
                pantalla de destino tiene el link del calendar y su propio flujo,
                y si el usuario vuelve sin agendar, insistirle con el cartel que
                ya tocó es ruido (mismo criterio que el modal de escritorio). */}
            <button
              type="button"
              onClick={() => {
                state.dismissCapacitacion?.();
                navigate("/capacitacion-ultra", { state: { desdeInicio: true } });
              }}
              className={`h-10 flex-shrink-0 px-4 text-[13.5px] font-medium sm:text-[15px] ${CTA_SUAVE}`}
            >
              Agendar
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // kind === "cuestionario": encuesta de feedback con premio de 10 imágenes.
  // Plan pago + ≥2 semanas de antigüedad + sin responder (ver useHomeBanners).
  // Mismo shell que "brand" y "tiendanube": tarjeta blanca con borde, ícono +
  // título en primary/negro y CTA sólido. Antes era una tarjeta azul sólida con
  // texto blanco y botón invertido, y en la home los tres banners se leían como
  // tres componentes distintos apilados.
  if (kind === "cuestionario") {
    return (
      <div className={className}>
        {/* Misma fila que "Mejorá tus fotos" también en MOBILE —
            ADR-2026-08-04-carteles-mobile-texto-mas-chico-y-cta-igual. Antes el CTA
            iba absoluto abajo a la derecha y el texto le reservaba 48px (`pb-12`),
            así que dos carteles con la misma anatomía —ícono, título, una acción— se
            leían como dos componentes distintos. */}
        <div
          className={`${mb} bg-white border border-neutral-200 rounded-2xl p-4 relative flex items-center justify-between gap-3`}
        >
          <div className="flex min-w-0 items-center gap-2">
            <Gift className="w-4 h-4 text-brand-primary shrink-0" />
            <h3 className="text-[14px] font-semibold text-brand-black/90 sm:text-[15px]">
              Obtené 10 imágenes gratis extra
            </h3>
          </div>
          {/* En mobile queda sólo la flecha, igual que el de marca: el título ya dice
              qué se reclama. `aria-label` para que el control no se quede sin nombre. */}
          <button
            type="button"
            aria-label="Reclamar"
            onClick={() => setShowCuestionario(true)}
            className={`shrink-0 text-[14px] font-medium ${CTA_ICONO}`}
          >
            <span className="hidden sm:inline">Reclamar</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        {idCompany && (
          <CuestionarioModal
            open={showCuestionario}
            idCompany={idCompany}
            onClose={() => {
              setShowCuestionario(false);
              if (cuestionarioDoneRef.current) state.dismissCuestionario?.();
            }}
            onSubmitted={() => {
              cuestionarioDoneRef.current = true;
            }}
          />
        )}
      </div>
    );
  }

  // kind === "escritorio": invitación a usar Atlas en una computadora. Sólo
  // llega acá en viewport de teléfono — el filtro vive en `useHomeBanners` y no
  // en un `sm:hidden` de este render, porque el `kind` es uno solo y esconder por
  // CSS al que ganó la cadena dejaría a los usuarios de escritorio con un hueco
  // invisible (ver el comentario del hook).
  //
  // Mismo shell que "tiendanube" —y no el de "brand"— porque son DOS acciones: en
  // mobile no entran en la fila del título, así que van abajo.
  if (kind === "escritorio") {
    return (
      <div className={className}>
        <div
          className={`${mb} bg-white border border-neutral-200 rounded-2xl p-4 relative sm:flex sm:items-center sm:justify-between sm:gap-3`}
        >
          <div className="pb-14 sm:pb-0">
            <div className="flex items-center gap-2 mb-0.5 sm:mb-1">
              <Monitor className="w-4 h-4 text-brand-primary shrink-0" />
              <h3 className="text-[14px] font-semibold text-brand-black/90 sm:text-[15px]">
                Probá Atlas en tu computadora
              </h3>
            </div>
            <p className="text-[13px] leading-snug text-[#3C3C43]/60 sm:text-[15px] sm:leading-normal">
              Vas a tener más modos disponibles y vas a generar más rápido.
            </p>
          </div>
          <div className="absolute bottom-4 left-4 right-4 flex items-center justify-end gap-2 sm:relative sm:bottom-auto sm:left-auto sm:right-auto sm:w-auto">
            <button
              type="button"
              onClick={() => state.dismissEscritorio?.()}
              className="inline-flex items-center justify-center h-10 px-3 text-[13.5px] font-medium text-[#3C3C43]/60 hover:text-brand-black/80 rounded-xl transition-colors flex-shrink-0 sm:text-[15px]"
            >
              No me interesa
            </button>
            <button
              type="button"
              onClick={() => setShowEscritorio(true)}
              className={`h-10 flex-shrink-0 px-4 text-[13.5px] font-medium sm:text-[15px] ${CTA_SUAVE}`}
            >
              Probar
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
        {/* Cerrar la pantalla también da el cartel por visto: ya leyó el mensaje.
            El orden importa — `dismissEscritorio` pone `kind` en null y esta rama
            deja de renderizarse, así que el modal se desmonta con ella. */}
        <ProbarEnEscritorioModal
          open={showEscritorio}
          onClose={() => {
            setShowEscritorio(false);
            state.dismissEscritorio?.();
          }}
        />
      </div>
    );
  }

  // kind === "tiendanube": aparece tras completar "Mejorá tus fotos", si la
  // empresa no tiene tienda conectada y el user no lo descartó.
  // Container con borde explícito (bg-brand-primary/[0.06] no compila — ver
  // memoria Tailwind del proyecto — así que el banner quedaba "flotando").
  if (kind === "tiendanube") {
    return (
      <div className={className}>
        <div
          className={`${mb} bg-white border border-neutral-200 rounded-2xl p-4 relative sm:flex sm:items-center sm:justify-between sm:gap-3`}
        >
          <div className="pb-14 sm:pb-0">
            <div className="flex items-center gap-2 mb-0.5 sm:mb-1">
              <TiendaNubeIcon className="h-4 w-auto text-[#2c3357]" />
              <h3 className="text-[14px] font-semibold text-brand-black/90 sm:text-[15px]">
                Conectá tu Tienda Nube
              </h3>
            </div>
            <p className="text-[13px] leading-snug text-[#3C3C43]/60 sm:text-[15px] sm:leading-normal">
              Importá tus productos y creá contenido para tu tienda en segundos.
            </p>
          </div>
          {/* Este conserva el CTA abajo: son DOS acciones ("No me interesa" y
              "Conectar") y no entran en la fila del título a 390px. */}
          <div className="absolute bottom-4 left-4 right-4 flex items-center justify-end gap-2 sm:relative sm:bottom-auto sm:left-auto sm:right-auto sm:w-auto">
            <button
              type="button"
              onClick={() => state.dismissTiendanube?.()}
              className="inline-flex items-center justify-center h-10 px-3 text-[13.5px] font-medium text-[#3C3C43]/60 hover:text-brand-black/80 rounded-xl transition-colors flex-shrink-0 sm:text-[15px]"
            >
              No me interesa
            </button>
            <button
              type="button"
              onClick={() => setShowTnConnect(true)}
              className={`h-10 flex-shrink-0 px-4 text-[13.5px] font-medium sm:text-[15px] ${CTA_SUAVE}`}
            >
              Conectar
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
        {/* companyHasStores={false}: el banner solo aparece si la empresa NO
            tiene NINGUNA tienda conectada (ver useHomeBanners). */}
        <TiendaNubeConnectModal
          open={showTnConnect}
          companyHasStores={false}
          connecting={tnConnecting}
          error={tnError}
          onCancel={() => {
            setTnError(null);
            setShowTnConnect(false);
          }}
          onConnect={handleTnConnect}
        />
      </div>
    );
  }

  // kind === "brand"
  return (
    <div className={className}>
      {/* En MOBILE es una FILA —texto a la izquierda, CTA a la derecha— y no un
          bloque con el botón colgado abajo (ADR-2026-08-04-banner-de-marca-mas-compacto).
          El botón absoluto obligaba a reservarle 48px de aire (`pb-12`) debajo del
          texto: medido a 390px de ancho, la tarjeta pasaba de 154px a 96px.
          En `sm:` para arriba no cambia nada: ahí ya era esta misma fila. */}
      {/* `justify-between` sin breakpoint —
          ADR-2026-08-04-cta-de-los-carteles-pegado-al-borde. Estaba en `sm:` y en
          mobile el botón quedaba pegado al texto, con el aire sobrante a su derecha.
          Acá no se notaba porque la descripción es larga y empuja sola; en la encuesta,
          que es un título corto, quedaba medio cartel en blanco. */}
      <div
        className={`${mb} bg-white border border-neutral-200 rounded-2xl p-4 relative flex items-center justify-between gap-3`}
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-0.5 sm:mb-1">
            <Sparkles className="w-4 h-4 shrink-0 text-brand-primary" />
            <h3 className="text-[14px] font-semibold text-brand-black/90 sm:text-[15px]">
              Mejorá tus fotos
            </h3>
          </div>
          <p className="text-[13px] leading-snug text-[#3C3C43]/60 sm:text-[15px] sm:leading-normal">
            Contale a la IA sobre tu marca para que genere fotos adaptadas a su
            estilo
          </p>
        </div>
        {/* En mobile queda sólo la flecha: el título ya dice qué se va a hacer, y
            "Mejorar fotos" al lado del texto obligaba a partirlo en cuatro líneas.
            `aria-label` para que el control siga teniendo nombre sin la etiqueta. */}
        <button
          type="button"
          aria-label="Mejorar fotos"
          onClick={() => navigate("/redes-sociales/descripcion-brand")}
          className={`shrink-0 text-[14px] font-medium ${CTA_ICONO}`}
        >
          <span className="hidden sm:inline">Mejorar fotos</span>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
